/**
 * The six drawings on /overview's "Inside the dashboard" (Sep 19 2026).
 *
 * Simplified pictures of features that are live today, drawn in the page's
 * own palette so each reads at a glance: a join link that reaches a student
 * and a mentor, the rows Enrollment shows, students sorted into cohorts, the
 * climb through the four outcome milestones, the board packet, and a figure
 * opening its "where this number comes from" drawer. They are pictures, not
 * screens: no figure in them is a claim, and the one real screen on the
 * section (the matching board) is a capture of the sample.
 *
 * Decorative by design (aria-hidden): the card's title and line say what the
 * picture shows. The dashed strokes carry `eg-sc-flow` and drift on hover,
 * and only when the reader has not asked for reduced motion (gateway.css,
 * section 16b).
 */

import type { ReactElement, ReactNode } from "react";

import type { ShowcaseArt as ArtKey } from "~/app/lib/partner";

const SANS = "Plus Jakarta Sans, ui-sans-serif, system-ui, sans-serif";
const HEADING = "Bricolage Grotesque, Plus Jakarta Sans, ui-sans-serif, sans-serif";

function Frame({ children }: { children: ReactNode }) {
  return (
    <svg viewBox="0 0 320 190" aria-hidden="true" focusable="false">
      {children}
    </svg>
  );
}

/** A small link glyph, drawn at (x, y). */
function LinkGlyph({ x, y, stroke }: { x: number; y: number; stroke: string }) {
  return (
    <g
      transform={`translate(${x} ${y})`}
      fill="none"
      stroke={stroke}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 11a4 4 0 0 0 5.7 0l3.4-3.4a4 4 0 0 0-5.7-5.7l-1.2 1.2" />
      <path d="M9 4.5a4 4 0 0 0-5.7 0L-0.1 7.9a4 4 0 0 0 5.7 5.7l1.2-1.2" />
    </g>
  );
}

/** A phone with a person and a green check: someone who joined. */
function JoinedPhone({ x, tint, ink }: { x: number; tint: string; ink: string }) {
  return (
    <g transform={`translate(${x} 0)`}>
      <rect x="0" y="106" width="48" height="80" rx="10" fill="#FFFFFF" stroke="#C9C5EE" strokeWidth={1.5} />
      <rect x="16" y="111" width="16" height="4" rx="2" fill="#E3E1F7" />
      <circle cx="24" cy="136" r="11" fill={tint} />
      <circle cx="24" cy="132.5" r="3.8" fill={ink} />
      <path d="M17 143a7 7 0 0 1 14 0z" fill={ink} />
      <rect x="10" y="154" width="28" height="13" rx="6.5" fill="#DDF3E4" />
      <path
        d="M18.5 160.5l2.5 2.5 5-5"
        fill="none"
        stroke="#1E7B4F"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  );
}

function JoinLink() {
  return (
    <Frame>
      <rect x="40" y="22" width="240" height="36" rx="18" fill="#FFFFFF" stroke="#C9C5EE" strokeWidth={1.5} />
      <LinkGlyph x={56} y={33} stroke="#6366F1" />
      <text x="80" y="44.5" fontFamily={SANS} fontSize="11.5" fontWeight="600" fill="#1A1A1A">
        join.pulseofp3.org/riverbend
      </text>
      <path
        className="eg-sc-flow p9"
        d="M150 58C150 86 110 80 110 104"
        fill="none"
        stroke="#6366F1"
        strokeWidth={2}
        strokeDasharray="4 5"
        strokeLinecap="round"
      />
      <path
        className="eg-sc-flow p9"
        d="M170 58C170 86 210 80 210 104"
        fill="none"
        stroke="#6366F1"
        strokeWidth={2}
        strokeDasharray="4 5"
        strokeLinecap="round"
      />
      <rect x="144" y="100" width="32" height="32" rx="6" fill="#FFFFFF" stroke="#C9C5EE" strokeWidth={1.5} />
      <rect x="149" y="105" width="8" height="8" rx="1.5" fill="none" stroke="#1A1A1A" strokeWidth={1.8} />
      <rect x="163" y="105" width="8" height="8" rx="1.5" fill="none" stroke="#1A1A1A" strokeWidth={1.8} />
      <rect x="149" y="119" width="8" height="8" rx="1.5" fill="none" stroke="#1A1A1A" strokeWidth={1.8} />
      <rect x="164" y="120" width="3" height="3" fill="#1A1A1A" />
      <rect x="168" y="124" width="3" height="3" fill="#1A1A1A" />
      <rect x="164" y="125" width="3" height="2" fill="#1A1A1A" />
      <rect x="159" y="116" width="3" height="3" fill="#1A1A1A" />
      <JoinedPhone x={86} tint="#E3E1F7" ink="#6366F1" />
      <JoinedPhone x={186} tint="#FBE3D0" ink="#D97706" />
      <text x="78" y="150" textAnchor="end" fontFamily={SANS} fontSize="9" fontWeight="800" letterSpacing="0.8" fill="#4F46E5">
        STUDENT
      </text>
      <text x="242" y="150" fontFamily={SANS} fontSize="9" fontWeight="800" letterSpacing="0.8" fill="#4F46E5">
        MENTOR
      </text>
    </Frame>
  );
}

/** One Enrollment row: an avatar, a name and a date as bars, and a status chip. */
function Row({
  y,
  avatar,
  name,
  chip,
  chipBg,
  chipInk,
  highlight = false,
}: {
  y: number;
  avatar: string;
  name: number;
  chip: string;
  chipBg: string;
  chipInk: string;
  highlight?: boolean;
}) {
  return (
    <g>
      {highlight ? <rect x="40" y={y - 14} width="240" height="29" rx="8" fill="#FFF6E8" /> : null}
      <circle cx="60" cy={y} r="9" fill={avatar} />
      <rect x="76" y={y - 6.5} width={name} height="6" rx="3" fill="#CFC8C1" />
      <rect x="76" y={y + 3.5} width="42" height="5" rx="2.5" fill="#E7E1DB" />
      <rect x="196" y={y - 8.5} width="76" height="17" rx="8.5" fill={chipBg} />
      <text x="234" y={y + 3.5} textAnchor="middle" fontFamily={SANS} fontSize="9" fontWeight="700" fill={chipInk}>
        {chip}
      </text>
    </g>
  );
}

function Waiting() {
  return (
    <Frame>
      <rect x="34" y="14" width="252" height="168" rx="14" fill="#FFFFFF" stroke="#F1D9B8" strokeWidth={1.5} />
      <text x="52" y="38" fontFamily={HEADING} fontSize="12" fontWeight="700" fill="#4A1020">
        Students
      </text>
      <Row y={65.5} avatar="#F6D9D9" name={64} chip="Has a mentor" chipBg="#E1F2E7" chipInk="#1E7B4F" />
      <Row y={96.5} avatar="#DCDCF6" name={58} chip="Waiting" chipBg="#FCEBD2" chipInk="#9A5B00" highlight />
      <line x1="48" y1="112.5" x2="272" y2="112.5" stroke="#F0ECE8" strokeWidth={1} />
      <Row y={127.5} avatar="#D8EEDD" name={70} chip="Has a mentor" chipBg="#E1F2E7" chipInk="#1E7B4F" />
      <line x1="48" y1="143.5" x2="272" y2="143.5" stroke="#F0ECE8" strokeWidth={1} />
      <Row y={158.5} avatar="#F9E5C8" name={52} chip="Not yet" chipBg="#EFEBE7" chipInk="#6B645E" />
    </Frame>
  );
}

/** Fifteen students before a cohort, placed by hand so the cloud looks loose. */
const LOOSE: readonly (readonly [number, number])[] = [
  [40, 62], [58, 48], [76, 62], [48, 82], [66, 78], [86, 86], [36, 104], [56, 100],
  [76, 108], [96, 108], [46, 126], [66, 122], [86, 130], [56, 146], [76, 144],
];

function Group({ y, label, count, color }: { y: number; label: string; count: number; color: string }) {
  return (
    <g>
      <rect x="150" y={y} width="150" height="40" rx="12" fill="#FFFFFF" stroke="#EBC9C9" strokeWidth={1.5} />
      <text x="164" y={y + 17} fontFamily={SANS} fontSize="10" fontWeight="700" fill="#4A1020">
        {label}
      </text>
      {Array.from({ length: count }, (_, i) => (
        <circle key={i} cx={168 + i * 12} cy={y + 29} r="4.5" fill={color} />
      ))}
    </g>
  );
}

function Cohorts() {
  return (
    <Frame>
      {LOOSE.map(([cx, cy]) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="5.5" fill="#C7BDB5" />
      ))}
      <path d="M110 96H138" fill="none" stroke="#D93A3A" strokeWidth={2.2} strokeLinecap="round" />
      <path d="M132 90l6 6-6 6" fill="none" stroke="#D93A3A" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" />
      <Group y={30} label="Department" count={5} color="#D93A3A" />
      <Group y={76} label="Semester" count={3} color="#6366F1" />
      <Group y={122} label="Program" count={4} color="#22A35A" />
    </Frame>
  );
}

/** A flag on the trail, with its label on a white pill. */
function Flag({ x, y, label, labelX, labelWidth }: { x: number; y: number; label: string; labelX: number; labelWidth: number }) {
  return (
    <g>
      <line x1={x} y1={y} x2={x} y2={y - 20} stroke="#4A1020" strokeWidth={1.6} />
      <polygon points={`${x},${y - 20} ${x + 12},${y - 16} ${x},${y - 12}`} fill="#D93A3A" />
      <circle cx={x} cy={y} r="3" fill="#1F6F78" />
      <rect x={labelX} y={y - 8} width={labelWidth} height="16" rx="8" fill="#FFFFFF" />
      <text x={labelX + labelWidth / 2} y={y + 3.5} textAnchor="middle" fontFamily={SANS} fontSize="9" fontWeight="700" fill="#1F6F78">
        {label}
      </text>
    </g>
  );
}

function Outcomes() {
  return (
    <Frame>
      <polygon points="0,190 92,76 128,108 206,32 320,190" fill="#BFE0C8" />
      <polygon points="206,32 224,56 212,62 198,50" fill="#E8F5EB" />
      <polygon points="92,76 104,90 94,96 84,88" fill="#E8F5EB" />
      <path
        className="eg-sc-flow p7"
        d="M22 184C60 176 72 154 96 148S138 132 150 124S188 104 196 90S212 60 206 46"
        fill="none"
        stroke="#1F6F78"
        strokeWidth={2.2}
        strokeDasharray="2 5"
        strokeLinecap="round"
      />
      <Flag x={96} y={148} label="Program" labelX={104} labelWidth={52} />
      <Flag x={150} y={124} label="Major" labelX={158} labelWidth={40} />
      <Flag x={196} y={90} label="Internship" labelX={206} labelWidth={62} />
      <Flag x={206} y={46} label="First role" labelX={222} labelWidth={60} />
    </Frame>
  );
}

function Packet() {
  const csvRows = [56, 68, 80, 92, 104];
  return (
    <Frame>
      <g transform="rotate(7 197 80)">
        <rect x="154" y="22" width="86" height="116" rx="8" fill="#FFFFFF" stroke="#E2CDCD" strokeWidth={1.5} />
        <rect x="202" y="32" width="30" height="14" rx="7" fill="#E1F2E7" />
        <text x="217" y="42" textAnchor="middle" fontFamily={SANS} fontSize="8.5" fontWeight="800" fill="#1E7B4F">
          CSV
        </text>
        {csvRows.map((y, row) => (
          <g key={y} fill={row === 0 ? "#D9CFC7" : "#EDE6E0"}>
            <rect x="164" y={y} width="18" height="5" rx="2.5" />
            <rect x="186" y={y} width="18" height="5" rx="2.5" />
            <rect x="208" y={y} width="22" height="5" rx="2.5" />
          </g>
        ))}
      </g>
      <g transform="rotate(-5 128 98)">
        <rect x="76" y="28" width="104" height="140" rx="8" fill="#FFFFFF" stroke="#E2CDCD" strokeWidth={1.5} />
        <rect x="76" y="28" width="104" height="32" rx="8" fill="#4A1020" />
        <rect x="76" y="46" width="104" height="14" fill="#4A1020" />
        <rect x="86" y="38" width="44" height="5" rx="2.5" fill="#FFFFFF" fillOpacity={0.85} />
        <rect x="134" y="38" width="18" height="5" rx="2.5" fill="#FF8A8A" />
        <rect x="86" y="48" width="30" height="4" rx="2" fill="#FFFFFF" fillOpacity={0.45} />
        {[86, 116, 146].map((x) => (
          <g key={x}>
            <rect x={x} y="68" width="26" height="22" rx="4" fill="#F6F1EE" />
            <rect x={x + 4} y="77" width="14" height="5" rx="2.5" fill="#4A4540" />
          </g>
        ))}
        <polygon points="86,138 102,130 118,132 134,118 150,112 170,100 170,150 86,150" fill="#F6D5D5" />
        <polyline
          points="86,138 102,130 118,132 134,118 150,112 170,100"
          fill="none"
          stroke="#D93A3A"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <rect x="138" y="154" width="30" height="13" rx="6.5" fill="#F7E3E3" />
        <text x="153" y="163.5" textAnchor="middle" fontFamily={SANS} fontSize="8.5" fontWeight="800" fill="#B82E2E">
          PDF
        </text>
      </g>
    </Frame>
  );
}

function Field({ y, label, bars }: { y: number; label: string; bars: readonly number[] }) {
  return (
    <g>
      <text x="176" y={y} fontFamily={SANS} fontSize="7" fontWeight="700" letterSpacing="0.5" fill="#9A938D">
        {label}
      </text>
      {bars.map((width, i) => (
        <rect key={i} x="176" y={y + 5 + i * 8} width={width} height="5" rx="2.5" fill="#E7E4F4" />
      ))}
    </g>
  );
}

function Trace() {
  return (
    <Frame>
      <rect x="18" y="46" width="128" height="98" rx="12" fill="#FFFFFF" stroke="#C9C5EE" strokeWidth={1.5} />
      <text x="32" y="70" fontFamily={SANS} fontSize="7.5" fontWeight="700" letterSpacing="0.6" fill="#76706B">
        STUDENTS CLAIMED
      </text>
      <text x="30" y="112" fontFamily={HEADING} fontSize="34" fontWeight="700" fill="#1A1A1A">
        140
      </text>
      <circle cx="110" cy="101" r="8" fill="#FFFFFF" stroke="#6366F1" strokeWidth={1.6} />
      <line x1="110" y1="99.5" x2="110" y2="105" stroke="#6366F1" strokeWidth={1.8} strokeLinecap="round" />
      <circle cx="110" cy="96.5" r="1.2" fill="#6366F1" />
      <rect x="32" y="124" width="70" height="5" rx="2.5" fill="#E7E1DB" />
      <path
        className="eg-sc-flow p7"
        d="M119 101C138 101 142 96 162 96"
        fill="none"
        stroke="#6366F1"
        strokeWidth={1.6}
        strokeDasharray="3 4"
        strokeLinecap="round"
      />
      <rect x="164" y="14" width="140" height="166" rx="12" fill="#FFFFFF" stroke="#C9C5EE" strokeWidth={1.5} />
      <text x="176" y="36" fontFamily={SANS} fontSize="7" fontWeight="800" letterSpacing="0.6" fill="#4F46E5">
        WHERE IT COMES FROM
      </text>
      <line x1="176" y1="44.5" x2="292" y2="44.5" stroke="#ECEBF7" strokeWidth={1} />
      <Field y={60} label="DEFINITION" bars={[104, 70]} />
      <Field y={92} label="SOURCE" bars={[96]} />
      <Field y={118} label="DENOMINATOR" bars={[108, 60]} />
      <Field y={152} label="KNOWN LIMITS" bars={[100]} />
    </Frame>
  );
}

const ART: Record<ArtKey, () => ReactElement> = {
  link: JoinLink,
  waiting: Waiting,
  cohorts: Cohorts,
  outcomes: Outcomes,
  packet: Packet,
  trace: Trace,
};

export function ShowcaseArt({ art }: { art: ArtKey }) {
  const Drawing = ART[art];
  return <Drawing />;
}
